import { ModalFunction, ModalFunctionProps } from './ModalFunction';
import { ReactNode } from 'react';
import {
  Button,
  ButtonProps,
  CloseButtonProps,
  CSSObject,
  Divider,
  DividerProps,
  HStack,
  Icon,
  IconProps,
  Modal as ChakraModal,
  ModalBody,
  ModalBodyProps,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalFooterProps,
  ModalHeader,
  ModalHeaderProps,
  ModalOverlay,
  ModalOverlayProps,
  ModalProps as ChakraModalProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineExclamationCircle,
  AiOutlineInfoCircle,
} from 'react-icons/ai/index.js';

export interface ModalProps extends ChakraModalProps {
  isOpen: boolean;
  onClose: () => void;
  overlayProps?: ModalOverlayProps;
  contentProps?: ModalContentProps;
  headerProps?: ModalHeaderProps;
  header?: ReactNode;
  showCloseButton?: boolean;
  showDivider?: boolean;
  closeButtonProps?: CloseButtonProps;
  dividerProps?: DividerProps;
  bodyProps?: ModalBodyProps;
  footerProps?: ModalFooterProps;
  footer?: ReactNode;
  showFooterBtns?: boolean;
  showOk?: boolean;
  okText?: React.ReactNode;
  onOk?: () => void;
  closeOnOk?: boolean;
  okButtonProps?: ButtonProps;
  okType?: ButtonProps['variant'];
  showCancel?: boolean;
  cancelText?: React.ReactNode;
  onCancel?: () => void;
  cancelButtonProps?: ButtonProps;
  containerProps?: CSSObject;
  icon?: React.ReactNode;
}

export const Modal = (props: ModalProps) => {
  const {
    isOpen,
    onClose,
    overlayProps,
    contentProps,
    headerProps,
    header,
    showCloseButton = true,
    showDivider = true,
    closeButtonProps,
    dividerProps,
    bodyProps,
    children,
    footerProps,
    footer,
    showOk = true,
    okText = 'Ok',
    onOk,
    closeOnOk = true,
    okButtonProps,
    okType = 'solid',
    showCancel = false,
    cancelText = 'Cancel',
    onCancel,
    cancelButtonProps,
    containerProps,
    showFooterBtns = showOk || showCancel,
    icon,
    ...rest
  } = props;

  const styles = useMultiStyleConfig('Modal', props);

  const handleCancel = () => {
    onClose();
    onCancel && onCancel();
  };
  const handleOk = () => {
    closeOnOk && onClose();
    onOk && onOk();
  };

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior='inside'
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(styles.ModalStyles as Record<string, any>)}
      {...rest}
    >
      <ModalOverlay sx={{ ...styles.OverlayStyles, ...overlayProps }} />
      <ModalContent
        sx={{ ...styles.ContentStyles, ...contentProps }}
        containerProps={{ sx: { ...containerProps } }}
      >
        {(header || icon) && (
          <ModalHeader sx={{ ...styles.HeaderStyles, ...headerProps }}>
            {icon}
            {header}
          </ModalHeader>
        )}
        {showCloseButton && (
          <ModalCloseButton
            sx={{ ...styles.CloseButtonStyles, ...closeButtonProps }}
          />
        )}
        {showDivider && (
          <Divider sx={{ ...styles.DividerStyles, ...dividerProps }} />
        )}
        {children && (
          <ModalBody sx={{ ...styles.BodyStyles, ...bodyProps }}>
            {children}
          </ModalBody>
        )}
        {showDivider && (
          <Divider sx={{ ...styles.DividerStyles, ...dividerProps }} />
        )}
        {(footer || showFooterBtns) && (
          <ModalFooter sx={{ ...styles.FooterStyles, ...footerProps }}>
            {footer ? (
              footer
            ) : showFooterBtns ? (
              <HStack>
                {showCancel && (
                  <Button
                    colorScheme='gray'
                    onClick={handleCancel}
                    boxShadow='lg'
                    {...cancelButtonProps}
                  >
                    {cancelText}
                  </Button>
                )}
                {showOk && (
                  <Button
                    colorScheme='blue'
                    variant={okType}
                    onClick={handleOk}
                    boxShadow='lg'
                    {...okButtonProps}
                  >
                    {okText}
                  </Button>
                )}
              </HStack>
            ) : (
              <></>
            )}
          </ModalFooter>
        )}
      </ModalContent>
    </ChakraModal>
  );
};

const commonIconStyles: IconProps = {
  fontSize: '22px',
  mr: 4,
  verticalAlign: '-5px',
};

Modal.default = (props: ModalFunctionProps) =>
  ModalFunction({ icon: null, closable: true, ...props });

Modal.confirm = (props: ModalFunctionProps) =>
  ModalFunction({
    icon: (
      <Icon
        as={AiOutlineExclamationCircle}
        {...commonIconStyles}
        color='orange.500'
      />
    ),
    showCancel: true,
    ...props,
  });
Modal.error = (props: ModalFunctionProps) =>
  ModalFunction({
    icon: (
      <Icon as={AiOutlineCloseCircle} {...commonIconStyles} color='red.500' />
    ),
    ...props,
  });
Modal.info = (props: ModalFunctionProps) =>
  ModalFunction({
    icon: (
      <Icon as={AiOutlineInfoCircle} {...commonIconStyles} color='blue.500' />
    ),
    ...props,
  });
Modal.success = (props: ModalFunctionProps) =>
  ModalFunction({
    icon: (
      <Icon as={AiOutlineCheckCircle} {...commonIconStyles} color='green.500' />
    ),
    ...props,
  });
Modal.warning = (props: ModalFunctionProps) =>
  ModalFunction({
    icon: (
      <Icon
        as={AiOutlineExclamationCircle}
        {...commonIconStyles}
        color='orange.500'
      />
    ),
    ...props,
  });
